'use client'

/**
 * Undo, step by step: ask the server how many copies an undo would put back (POST
 * /actions/:jobId/undo/preview), say what an undo does in plain words, confirm, then undo (POST
 * /actions/:jobId/undo). The Search & Remediate page and the activity dock both undo through this hook,
 * so the two agree on the words, the numbers and the refusals.
 *
 * AN UNDO PUTS MAIL BACK. Each copy returns to exactly the folder (or Gmail labels) it was in before our
 * move, and for most copies that is a person's inbox. The dialog says so BEFORE anything moves, and above
 * 25 copies the analyst types the count the preview showed (the shared TypedConfirmDialog, digits folded)
 * and the confirm carries the preview's token. At 25 or fewer there is one plain confirmation and no body.
 *
 * The analyst never confirms numbers they did not see: when the server says the count moved or the token
 * lapsed, the flow previews again and shows the new numbers; when asking again cannot help (the undo
 * already exists, there is nothing left to put back), it says so and closes.
 *
 * A PERMANENT DELETE cannot be undone (owner, 27 Oct 2026). Its undo preview puts back nothing and says how
 * many copies were deleted for good (`permanentlyDeleted`); the flow shows exactly that, with no way to
 * confirm anything, and the server refuses an undo of it (er_snr_not_undoable) whatever the page does.
 */

import * as React from 'react'
import { toast } from 'sonner'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { TypedConfirmDialog } from '@/components/ui/typed-confirm-dialog'
import { useTranslation } from '@/lib/i18n'
import { useSnrUndo, useSnrUndoPreview } from '@/lib/er/hooks/use-search-remediate-v2'
import { refuseSnr, snrErrorCodeOf, snrErrorKey } from '@/lib/er/snr-refusal'
import { normalizeTypedCount, typedCountPhrase } from '@/lib/er/snr-query/typed-count'
import type { SnrUndoPreview } from '@/lib/er/snr-query/types'

/** The numbers or the token moved: show the new ones, never act on the old. */
const PREVIEW_AGAIN = new Set(['er_snr_count_mismatch', 'er_snr_confirm_expired', 'er_snr_confirm_invalid', 'er_snr_confirm_required'])
/** Asking again cannot help: the undo exists already, there is nothing left to put back, or it was a permanent delete. */
const CLOSE_AND_SAY = new Set([
  'er_snr_confirm_used',
  'er_snr_already_undone',
  'er_snr_nothing_to_undo',
  'er_snr_job_still_running',
  'er_snr_job_not_found',
  'er_snr_not_undoable',
])

interface Active {
  jobId: string
  preview: SnrUndoPreview
  /** Counts previews, so a new one remounts the dialog and the typed count starts empty again. */
  seq: number
}

export function useSnrUndoFlow(runId: string, onChildJob: (undoJobId: string) => void) {
  const { t } = useTranslation()
  const previewMut = useSnrUndoPreview()
  const undoMut = useSnrUndo(runId)
  const [active, setActive] = React.useState<Active | null>(null)
  // A permanent delete's undo preview: how many copies are gone for good (nothing to confirm).
  const [gone, setGone] = React.useState<number | null>(null)

  const requestUndo = React.useCallback(
    (jobId: string) => {
      previewMut.mutate(jobId, {
        onSuccess: (preview) => {
          if (preview.reversible === false) {
            setActive(null)
            setGone(preview.permanentlyDeleted ?? 0)
            return
          }
          if (preview.messages <= 0) {
            toast.error(t(snrErrorKey('er_snr_nothing_to_undo')))
            setActive(null)
            return
          }
          setActive((prev) => ({ jobId, preview, seq: (prev?.seq ?? 0) + 1 }))
        },
        onError: refuseSnr(t, 'Previewing the undo', 'er.snr.job.undoFailed'),
      })
    },
    [previewMut, t],
  )

  const confirm = () => {
    if (!active) return
    const { jobId, preview } = active
    undoMut.mutate(
      {
        jobId,
        // Above 25 copies the preview's token and the typed count are required; at or below, no body at all.
        ...(preview.requiresTypedCount && preview.confirmToken
          ? { confirmToken: preview.confirmToken, typedCount: preview.messages }
          : {}),
      },
      {
        onSuccess: ({ jobId: childJobId }) => {
          toast.success(t('er.snr.toast.undoQueued', { count: preview.messages }))
          setActive(null)
          onChildJob(childJobId)
        },
        onError: (err) => {
          const code = snrErrorCodeOf(err)
          if (code && PREVIEW_AGAIN.has(code)) {
            toast.error(t(snrErrorKey(code)))
            requestUndo(jobId)
            return
          }
          if (code && CLOSE_AND_SAY.has(code)) {
            toast.error(t(snrErrorKey(code)))
            setActive(null)
            return
          }
          refuseSnr(t, 'Undoing the action', 'er.snr.job.undoFailed')(err)
        },
      },
    )
  }

  const dialogs = active ? (
    <SnrUndoDialog
      key={active.seq}
      preview={active.preview}
      isPending={undoMut.isPending || previewMut.isPending}
      onOpenChange={(o) => !o && !undoMut.isPending && setActive(null)}
      onConfirm={confirm}
    />
  ) : gone !== null ? (
    <SnrNotUndoableDialog count={gone} onClose={() => setGone(null)} />
  ) : null

  return { requestUndo, dialogs, isPending: previewMut.isPending || undoMut.isPending }
}

/** A permanent delete has no undo: how many copies are gone, and nothing to confirm. */
function SnrNotUndoableDialog({ count, onClose }: { count: number; onClose: () => void }) {
  const { t } = useTranslation()
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-snr-not-undoable>
        <DialogHeader>
          <DialogTitle>{t('er.snr.undo.permanentTitle')}</DialogTitle>
          <DialogDescription>{t('er.snr.undo.permanentBody', { count })}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={onClose}>{t('er.snr.undo.permanentClose')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The confirmation of an undo. Plain words first: where the copies go, that for most that is an inbox, and that
 * only the copies the action moved are touched. Above 25 copies it asks for the count.
 */
function SnrUndoDialog({
  preview,
  isPending,
  onOpenChange,
  onConfirm,
}: {
  preview: SnrUndoPreview
  isPending: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}) {
  const { t } = useTranslation()
  const body = (
    <div className="space-y-3 text-start">
      <p className="text-sm text-foreground" data-snr-undo-body>
        {t('er.snr.undo.body')}
      </p>
      <Alert variant="warning" data-snr-undo-inboxes>
        <AlertTitle>{t('er.snr.undo.inboxTitle')}</AlertTitle>
        <AlertDescription>{t('er.snr.undo.inboxWarning')}</AlertDescription>
      </Alert>
      <p className="text-xs text-muted-foreground" data-snr-undo-only-moved>
        {t('er.snr.undo.onlyMoved')}
      </p>
    </div>
  )
  const title = t('er.snr.undo.title', { count: preview.messages })
  const confirmLabel = t('er.snr.undo.confirm')

  if (preview.requiresTypedCount) {
    return (
      <TypedConfirmDialog
        normalizeInput={normalizeTypedCount}
        open
        onOpenChange={onOpenChange}
        phrase={typedCountPhrase(preview.messages)}
        title={title}
        description={body}
        confirmLabel={confirmLabel}
        cancelLabel={t('common.cancel')}
        variant="warning"
        isPending={isPending}
        onConfirm={onConfirm}
      />
    )
  }
  return (
    <ConfirmDialog
      open
      onOpenChange={onOpenChange}
      title={title}
      description={body}
      confirmLabel={confirmLabel}
      cancelLabel={t('common.cancel')}
      busy={isPending}
      busyLabel={t('er.snr.confirm.processing')}
      onConfirm={onConfirm}
    />
  )
}
